// H.R.M.E.Z config. The full web feature spans 7 dedicated tables plus a
// Google Drive OAuth redirect flow (needs expo-auth-session on mobile) — that
// is a standalone project, not a settings form. This screen ports the
// straightforward part: the classification list CRUD (hrmez_classifications),
// and is explicit that Drive connection + routing rules stay on web for now.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GoldButton, GlassCard } from '../../v10/ui';
import { supabase } from '../../lib/supabase';

interface Classification {
  id: string;
  display_text: string;
  color: string | null;
  target_type: string | null;
  sort_order: number | null;
}

export default function HrmezEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { c } = useTheme();
  const [rows, setRows] = useState<Classification[]>([]);
  const [newText, setNewText] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from('hrmez_classifications')
      .select('id, display_text, color, target_type, sort_order')
      .eq('user_id', userId)
      .order('sort_order');
    setRows((data as Classification[]) ?? []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    const text = newText.trim();
    if (!text || !userId) return;
    const { data } = await supabase
      .from('hrmez_classifications')
      .insert({ user_id: userId, display_text: text, color: '#9ba1aa', sort_order: rows.length })
      .select('id, display_text, color, target_type, sort_order')
      .single();
    if (data) setRows((r) => [...r, data as Classification]);
    setNewText('');
  };

  const update = async (id: string, patch: Partial<Classification>) => {
    setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    await supabase.from('hrmez_classifications').update(patch).eq('id', id);
  };

  const remove = (id: string) => {
    Alert.alert('Delete classification?', undefined, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setRows((r) => r.filter((x) => x.id !== id));
          await supabase.from('hrmez_classifications').delete().eq('id', id);
        },
      },
    ]);
  };

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <GlassCard style={styles.banner}>
        <Text style={styles.bannerText}>
          Google Drive connection and file-routing rules are set up on the web app. Here you can edit
          your pad classifications.
        </Text>
      </GlassCard>

      <SectionTitle style={styles.sectionGap}>Classifications</SectionTitle>
      {loading ? (
        <Text style={styles.hint}>Loading…</Text>
      ) : (
        rows.map((row) => (
          <GlassCard key={row.id} style={styles.card}>
            <View style={styles.rowBetween}>
              <View style={[styles.swatch, { backgroundColor: row.color ?? c.textFaint }]} />
              <Field
                inputStyle={styles.nameInput}
                value={row.display_text}
                onChangeText={(t) => setRows((r) => r.map((x) => (x.id === row.id ? { ...x, display_text: t } : x)))}
                onEndEditing={() => update(row.id, { display_text: row.display_text })}
              />
            </View>
            <View style={styles.rowBetween}>
              <Text style={styles.fieldLabel}>Color (hex)</Text>
              <Field
                inputStyle={styles.hexInput}
                value={row.color ?? ''}
                autoCapitalize="none"
                onChangeText={(t) => setRows((r) => r.map((x) => (x.id === row.id ? { ...x, color: t } : x)))}
                onEndEditing={() => update(row.id, { color: row.color })}
                placeholder="#9ba1aa"
              />
            </View>
            <Pressable onPress={() => remove(row.id)}>
              <Text style={styles.deleteText}>Delete</Text>
            </Pressable>
          </GlassCard>
        ))
      )}

      <View style={styles.addRow}>
        <Field inputStyle={styles.addInput} value={newText} onChangeText={setNewText} placeholder="New classification" />
        <GoldButton label="Add" onPress={() => void add()} style={styles.actionGap} />
      </View>
    </ScrollView>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  content: { paddingHorizontal: 16, paddingBottom: 48, gap: 8 },
  banner: {
    padding: 12,
    marginTop: 8,
  },
  bannerText: { fontSize: 12, color: t.goldBright, lineHeight: 17 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  card: {
    padding: 12,
    marginTop: 8,
    gap: 8,
  },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  swatch: { width: 20, height: 20, borderRadius: 6 },
  nameInput: {
    flex: 1,
    fontWeight: '700',
  },
  fieldLabel: { fontSize: 13, color: t.ivorySoft },
  hexInput: {
    minWidth: 90,
    textAlign: 'right',
  },
  deleteText: { fontSize: 13, color: t.lapsed, fontWeight: '700', marginTop: 2 },
  addRow: { flexDirection: 'row', gap: 8, marginTop: 12 },
  addInput: {
    flex: 1,
  },
}),
);
